import axios, {AxiosResponse} from 'axios'
import {
  ApiMessage,
  CreateInternalRolePayload,
  FeatureFormPayload,
  FeatureNode,
  FeaturePermissionInput,
  InternalUser,
  Pagination,
  RoleWithMeta,
  UserFeatureInput,
  UserFeatureState,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL

const ROLES_URL = `${API_URL}/roles`

interface DataResponse<T> {
  data: T
  payload?: {pagination?: Pagination}
}

export const getFeatureCatalog = (): Promise<FeatureNode[]> =>
  axios.get(`${ROLES_URL}/features-catalog`).then((d: AxiosResponse<DataResponse<FeatureNode[]>>) => d.data.data)

export const getFeatureRegistry = (): Promise<FeatureNode[]> =>
  axios.get(`${ROLES_URL}/features-registry`).then((d: AxiosResponse<DataResponse<FeatureNode[]>>) => d.data.data)

export const createFeature = (payload: FeatureFormPayload): Promise<ApiMessage> =>
  axios.post(`${ROLES_URL}/features`, payload).then((d: AxiosResponse<ApiMessage>) => d.data)

export const updateFeature = (id: number, payload: FeatureFormPayload): Promise<ApiMessage> =>
  axios.put(`${ROLES_URL}/features/${id}`, payload).then((d: AxiosResponse<ApiMessage>) => d.data)

export const deleteFeature = (id: number): Promise<ApiMessage> =>
  axios.delete(`${ROLES_URL}/features/${id}`).then((d: AxiosResponse<ApiMessage>) => d.data)

export const getRolesWithMeta = (): Promise<RoleWithMeta[]> =>
  axios.get(`${ROLES_URL}/list-with-meta`).then((d: AxiosResponse<DataResponse<RoleWithMeta[]>>) => d.data.data)

export const getRolePermissions = (roleId: string): Promise<FeatureNode[]> =>
  axios.get(`${ROLES_URL}/permissions/${roleId}`).then((d: AxiosResponse<DataResponse<FeatureNode[]>>) => d.data.data)

export const createInternalRole = (payload: CreateInternalRolePayload): Promise<ApiMessage> =>
  axios.post(`${ROLES_URL}/create-internal`, payload).then((d: AxiosResponse<ApiMessage>) => d.data)

export const saveRolePermissions = (
  roleId: string,
  permissions: FeaturePermissionInput[]
): Promise<ApiMessage> =>
  axios.put(`${ROLES_URL}/permissions/${roleId}`, {permissions}).then((d: AxiosResponse<ApiMessage>) => d.data)

export const updateRole = (payload: {id: string; name: string; userType: string; description?: string}): Promise<ApiMessage> =>
  axios.put(`${ROLES_URL}/Update`, payload).then((d: AxiosResponse<ApiMessage>) => d.data)

export const deleteRole = (roleId: string): Promise<ApiMessage> =>
  axios.delete(`${ROLES_URL}/DeleteRole/${roleId}`).then((d: AxiosResponse<ApiMessage>) => d.data)

export const getMyNavigation = (): Promise<FeatureNode[]> =>
  axios.get(`${ROLES_URL}/my-navigation`).then((d: AxiosResponse<DataResponse<FeatureNode[]>>) => d.data.data)

export const getInternalUsers = (
  page: number,
  itemsPerPage: number,
  search: string,
  roleId?: string
): Promise<DataResponse<InternalUser[]>> =>
  axios
    .get(`${ROLES_URL}/internal-users`, {params: {page, items_per_page: itemsPerPage, search, roleId}})
    .then((d: AxiosResponse<DataResponse<InternalUser[]>>) => d.data)

export const assignUsers = (roleId: string, userIds: string[]): Promise<ApiMessage> =>
  axios.post(`${ROLES_URL}/assign-users`, {roleId, userIds}).then((d: AxiosResponse<ApiMessage>) => d.data)

export const reassignUser = (userId: string, roleId: string): Promise<ApiMessage> =>
  axios.post(`${ROLES_URL}/reassign-user`, {userId, roleId}).then((d: AxiosResponse<ApiMessage>) => d.data)

export const removeUserFromRole = (userId: string, roleId: string): Promise<ApiMessage> =>
  axios.post(`${ROLES_URL}/remove-user`, {userId, roleId}).then((d: AxiosResponse<ApiMessage>) => d.data)

export const getUserFeatureStates = (userId: string): Promise<UserFeatureState[]> =>
  axios
    .get(`${ROLES_URL}/user-features/${userId}`)
    .then((d: AxiosResponse<DataResponse<UserFeatureState[]>>) => d.data.data)

export const saveUserFeatures = (
  userId: string,
  features: UserFeatureInput[]
): Promise<ApiMessage> =>
  axios
    .put(`${ROLES_URL}/user-features/${userId}`, {features})
    .then((d: AxiosResponse<ApiMessage>) => d.data)
